import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface CreativeDrawerContextState {
  currentAdId: string | null;
  openCreativeDrawer: (adId: string) => void;
  closeCreativeDrawer: () => void;
}

const Context = createContext<CreativeDrawerContextState | null>(null);

export function CreativeDrawerContextProvider({ children }: { readonly children: ReactNode }) {
  const [currentAdId, setCurrentAdId] = useState<string | null>(null);

  const openCreativeDrawer = useCallback((adId: string) => {
    setCurrentAdId(adId);
  }, []);

  const closeCreativeDrawer = useCallback(() => setCurrentAdId(null), []);

  const value = useMemo<CreativeDrawerContextState>(
    () => ({ currentAdId, openCreativeDrawer, closeCreativeDrawer }),
    [currentAdId, openCreativeDrawer, closeCreativeDrawer],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCreativeDrawerContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useCreativeDrawerContext hook used outside <CreativeDrawerContextProvider />',
    );
  }

  return context;
}

export function useOptionalCreativeDrawerContext() {
  return useContext(Context);
}
